import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import customerService from "../services/customerService";

const errMsg = (error, fallback) => error.response?.data?.message || fallback;

export const fetchCustomers = createAsyncThunk(
  "customer/fetchCustomers",
  async (_, thunkAPI) => {
    try {
      const res = await customerService.getCustomers();
      if (res.status !== true) return thunkAPI.rejectWithValue(res);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errMsg(error, "Failed to load customers") });
    }
  }
);

export const fetchCustomer = createAsyncThunk(
  "customer/fetchCustomer",
  async (id, thunkAPI) => {
    try {
      const res = await customerService.showCustomer(id);
      if (res.status !== true) return thunkAPI.rejectWithValue(res);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errMsg(error, "Failed to load customer") });
    }
  }
);

export const addCustomer = createAsyncThunk(
  "customer/addCustomer",
  async (data, thunkAPI) => {
    try {
      const res = await customerService.createCustomer(data);
      if (res.status !== true) return thunkAPI.rejectWithValue(res);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { message: "Failed to create customer" }
      );
    }
  }
);

export const editCustomer = createAsyncThunk(
  "customer/editCustomer",
  async (data, thunkAPI) => {
    try {
      const res = await customerService.updateCustomer(data);
      if (res.status !== true) return thunkAPI.rejectWithValue(res);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { message: "Failed to update customer" }
      );
    }
  }
);

export const removeCustomer = createAsyncThunk(
  "customer/removeCustomer",
  async (id, thunkAPI) => {
    try {
      const res = await customerService.deleteCustomer(id);
      if (res.status !== true) return thunkAPI.rejectWithValue(res);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errMsg(error, "Failed to delete customer") });
    }
  }
);

const initialState = {
  customers: [],
  customer: null,
  isLoading: false,   // list load
  isSaving: false,    // create / update / delete
  isError: false,
  message: "",
};

const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    clearCustomerError: (state) => {
      state.isError = false;
      state.message = "";
    },
    clearCustomer: (state) => {
      state.customer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // list
      .addCase(fetchCustomers.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload || [];
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload?.message || "Failed to load customers";
      })

      // show
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        state.customer = action.payload;
      })
      .addCase(fetchCustomer.rejected, (state, action) => {
        state.isError = true;
        state.message = action.payload?.message || "Failed to load customer";
      })

      // create
      .addCase(addCustomer.pending, (state) => {
        state.isSaving = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        state.isSaving = false;
        state.customers = [action.payload, ...state.customers];
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.isSaving = false;
        state.isError = true;
        state.message = action.payload?.message || "Failed to create customer";
      })

      // update
      .addCase(editCustomer.pending, (state) => {
        state.isSaving = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(editCustomer.fulfilled, (state, action) => {
        state.isSaving = false;
        state.customers = state.customers.map((c) =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        );
      })
      .addCase(editCustomer.rejected, (state, action) => {
        state.isSaving = false;
        state.isError = true;
        state.message = action.payload?.message || "Failed to update customer";
      })

      // delete
      .addCase(removeCustomer.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(removeCustomer.fulfilled, (state, action) => {
        state.isSaving = false;
        state.customers = state.customers.filter((c) => c.id !== action.payload);
      })
      .addCase(removeCustomer.rejected, (state, action) => {
        state.isSaving = false;
        state.isError = true;
        state.message = action.payload?.message || "Failed to delete customer";
      });
  },
});

export const { clearCustomerError, clearCustomer } = customerSlice.actions;
export default customerSlice.reducer;